import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["form", "input", "submitButton", "messagesContainer", "loadingIndicator"]
  static values = { conversationId: Number }

  connect() {
    this.scrollToBottom()
    this.setupCableSubscription()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  setupCableSubscription() {
    this.subscription = consumer.subscriptions.create(
      {
        channel: "GenaiUi::ConversationChannel",
        conversation_id: this.conversationIdValue
      },
      {
        received: (data) => this.handleCableMessage(data)
      }
    )
  }

  submit(event) {
    event.preventDefault()
    
    const content = this.inputTarget.value.trim()
    if (!content) {
      return
    }

    this.setLoading(true)
    
    const url = this.formTarget.action

    fetch(url, {
      method: 'POST',
      body: JSON.stringify({ message: { content: content } }),
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.status === 'success') {
        this.appendUserMessage(data.message)
        this.clearInput()
      } else {
        alert('Error: ' + (data.errors ? data.errors.join(', ') : 'Failed to send message'))
        this.setLoading(false)
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Failed to send message. Please try again.')
      this.setLoading(false)
    })
  }

  handleCableMessage(data) {
    if (data.type === 'new_message') {
      this.appendAssistantMessage(data.message)
      this.setLoading(false)
    } else if (data.type === 'message_failed') {
      this.showErrorState(data.message)
      this.setLoading(false)
    }
  }

  appendUserMessage(message) {
    const messageHtml = this.createMessageHtml(message, 'user')
    this.messagesContainerTarget.insertAdjacentHTML('beforeend', messageHtml)
    this.scrollToBottom()
  }

  appendAssistantMessage(message) {
    const messageHtml = this.createMessageHtml(message, 'assistant')
    this.messagesContainerTarget.insertAdjacentHTML('beforeend', messageHtml)
    this.scrollToBottom()
  }

  createMessageHtml(message, role) {
    const timeAgo = this.timeAgoInWords(new Date(message.created_at))
    return `
      <div class="genai-ui-message genai-ui-message-${role}" data-message-id="${message.id}">
        <div class="message-content">
          ${this.formatContent(message.content)}
        </div>
        <div class="message-meta">
          <span class="message-role">${role === 'user' ? 'You' : 'Assistant'}</span>
          <span class="message-time">${timeAgo} ago</span>
        </div>
      </div>
    `
  }

  showErrorState(message) {
    const errorHtml = `
      <div class="genai-ui-message genai-ui-message-assistant" data-message-id="${message.id}">
        <div class="message-content">
          <p style="color: #dc3545;">Failed to generate response. Please try again.</p>
        </div>
        <div class="message-meta">
          <span class="message-role">Assistant</span>
          <span class="message-error">${message.error_message || 'Unknown error'}</span>
        </div>
      </div>
    `
    this.messagesContainerTarget.insertAdjacentHTML('beforeend', errorHtml)
    this.scrollToBottom()
  }

  formatContent(content) {
    // Simple paragraph formatting
    return content.split('\n\n').map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('')
  }

  timeAgoInWords(date) {
    const seconds = Math.floor((new Date() - date) / 1000)
    
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    }
    
    for (const [name, seconds_in_interval] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / seconds_in_interval)
      if (interval >= 1) {
        return interval === 1 ? `1 ${name}` : `${interval} ${name}s`
      }
    }
    
    return 'just now'
  }

  setLoading(loading) {
    this.submitButtonTarget.disabled = loading
    this.inputTarget.disabled = loading
    this.loadingIndicatorTarget.style.display = loading ? 'flex' : 'none'
  }

  clearInput() {
    this.inputTarget.value = ''
    this.inputTarget.focus()
  }

  scrollToBottom() {
    this.messagesContainerTarget.scrollTop = this.messagesContainerTarget.scrollHeight
  }
}
